import type { DigitalMenuCategory, DigitalMenuOptionGroup, DigitalMenuProduct, DigitalMenuProductDetail } from 'shared-models';
import { getProductMenuCategoryIds, roundMoney } from 'shared-models/browser';
import { priceAndBuild } from '../../utils/variant-combo-pricing';

export type DigitalMenuBasketLine = {
  key: string;
  productId: number;
  name: string;
  imageUrl: string | null;
  unitPrice: number;
  quantity: number;
  picks: number[];
  choices: string[];
};

export const menuMoney = (amount: number) => new Intl.NumberFormat('he-IL', {
  style: 'currency', currency: 'ILS', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(amount);

export function pickCounts(picks: number[]): Record<number, number> {
  return picks.reduce<Record<number, number>>((counts, id) => { counts[id] = (counts[id] ?? 0) + 1; return counts; }, {});
}

export function defaultPicks(groups: DigitalMenuOptionGroup[]): number[] {
  return groups.flatMap((group) => group.items.filter((item) => item.inStock && item.isDefault && item.maxPick !== 0)
    .slice(0, group.pickLimit).flatMap((item) => [item.id, ...defaultPicks(item.childGroups)]));
}

function subtreeIds(groups: DigitalMenuOptionGroup[]): number[] {
  return groups.flatMap((group) => group.items.flatMap((item) => [item.id, ...subtreeIds(item.childGroups)]));
}

export function changePick(group: DigitalMenuOptionGroup, itemId: number, picks: number[], delta: 1 | -1): number[] {
  const item = group.items.find((entry) => entry.id === itemId);
  if (!item) return picks;
  const counts = pickCounts(picks);
  const quantity = counts[itemId] ?? 0;
  if (delta === -1) {
    const index = picks.lastIndexOf(itemId);
    if (index < 0) return picks;
    const childIds = quantity === 1 ? new Set(subtreeIds(item.childGroups)) : new Set<number>();
    return picks.filter((id, position) => position !== index && !childIds.has(id));
  }
  if (!item.inStock || quantity >= Math.min(item.maxPick ?? group.pickLimit, group.pickLimit)) return picks;
  if (group.pickLimit === 1) {
    const ids = new Set(group.items.flatMap((entry) => [entry.id, ...subtreeIds(entry.childGroups)]));
    return [...picks.filter((id) => !ids.has(id)), itemId, ...defaultPicks(item.childGroups)];
  }
  const total = group.items.reduce((sum, entry) => sum + (counts[entry.id] ?? 0), 0);
  if (total >= group.pickLimit) return picks;
  return [...picks, itemId, ...(quantity === 0 ? defaultPicks(item.childGroups) : [])];
}

export function validChoices(groups: DigitalMenuOptionGroup[], counts: Record<number, number>): boolean {
  return groups.every((group) => {
    const total = group.items.reduce((sum, item) => sum + (counts[item.id] ?? 0), 0);
    return total >= group.minPick && total <= group.pickLimit && group.items.every((item) => {
      const quantity = counts[item.id] ?? 0;
      return quantity === 0 || (item.inStock && quantity <= (item.maxPick ?? group.pickLimit) && validChoices(item.childGroups, counts));
    });
  });
}

export function configuredDish(product: DigitalMenuProductDetail, picks: number[], quantity: number): DigitalMenuBasketLine {
  const counts = pickCounts(picks);
  const { extra } = priceAndBuild(product.groups, counts, picks);
  const unitPrice = roundMoney(product.price + extra);
  const choices: string[] = [];
  const walk = (groups: DigitalMenuOptionGroup[]) => groups.forEach((group) => group.items.forEach((item) => {
    if (counts[item.id]) { choices.push(`${item.name}${counts[item.id] > 1 ? ` ×${counts[item.id]}` : ''}`); walk(item.childGroups); }
  }));
  walk(product.groups);
  return { key: JSON.stringify([product.id, unitPrice, picks]), productId: product.id,
    name: product.name, imageUrl: product.imageUrl, unitPrice, quantity, picks: [...picks], choices };
}

export function basketTotal(lines: DigitalMenuBasketLine[]): number {
  return roundMoney(lines.reduce((sum, line) => sum + roundMoney(line.unitPrice * line.quantity), 0));
}

export function groupMenuProducts(products: DigitalMenuProduct[], categories: DigitalMenuCategory[], selectedCategoryId?: number, search = '') {
  const groups = new Map<number | null, { categoryId: number | null; name: string; products: DigitalMenuProduct[] }>();
  const seenProductIds = new Set<number>();
  for (const product of products) {
    const memberships = getProductMenuCategoryIds({ categoryId: product.categoryId, secondaryCategoryIds: product.secondaryCategories?.map(category => category.id) });
    const categoryIds: Array<number | null> = memberships.length > 0 ? memberships : [null];
    for (const categoryId of categoryIds) {
      if (selectedCategoryId !== undefined && categoryId !== selectedCategoryId) continue;
      if (search.trim() && seenProductIds.has(product.id)) continue;
      let group = groups.get(categoryId);
      if (!group) {
        const name = categoryId === product.categoryId ? product.categoryName : product.secondaryCategories?.find(category => category.id === categoryId)?.name;
        group = { categoryId,
          name: categoryId === null ? 'מנות נוספות' : name ?? categories.find(category => category.id === categoryId)?.name ?? 'מנות',
          products: [] };
        groups.set(categoryId, group);
      }
      group.products.push(product);
      seenProductIds.add(product.id);
    }
  }
  return [...groups.values()];
}